import React, { useState, useEffect } from "react";
import {
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Box,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useAtom } from "jotai";
import {
  themeAtom,
  isElectronAtom,
  currentStaffDataAtom,
  listsAtom,
} from "../../../../-Atoms";
import {
  db,
  updateDoc,
  doc,
  arrayUnion,
  getDoc,
} from "../../../../firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import getJobName from "../../../../GlobalFunctions/getJobName";
import { getDropboxUrlWithRetry } from "../../../../DropboxFunctions";

const { DateTime } = require("luxon");

const StartShootingButton = ({
  jobData,
  clientData,
  idList,
  allMultipleDayJobs = [],
}) => {
  const [theme] = useAtom(themeAtom);
  const [isElectron] = useAtom(isElectronAtom);
  const [lists] = useAtom(listsAtom);

  // Calculate current list ID for this job
  const [currentIdList, setCurrentIdList] = useState("");

  useEffect(() => {
    let foundListId = "";
    
    if (lists && jobData.id) {
      for (const list of lists) {
        if (list.items && list.items.includes(jobData.id)) {
          foundListId = list.id;
          break;
        }
      }
    }
    
    setCurrentIdList(foundListId);
  }, [lists, jobData.id]);

  const [startShootingButtonText, setStartShootingButtonText] = useState("Start Shooting");
  const [startShootingDisabled, setStartShootingDisabled] = useState(false);
  const [shouldCreateSession, setShouldCreateSession] = useState(() => {
    if (
      jobData?.jobTypeFull?.includes("-V") ||
      jobData?.jobTypeFull?.includes("-E")
    ) {
      return false;
    }
    return isElectron;
  });

  // Update shouldCreateSession when job type changes
  useEffect(() => {
    if (
      jobData?.jobTypeFull?.includes("-V") ||
      jobData?.jobTypeFull?.includes("-E")
    ) {
      setShouldCreateSession(false);
    }
  }, [jobData?.jobTypeFull]);

  // Check if button should be visible - use real-time currentIdList
  const shouldShowButton = !currentIdList && jobData?.id && jobData?.client && 
    (jobData?.studio?.includes("Studio") || jobData?.studio?.includes("Location"));

  const startShooting = async () => {
    try {
      if (startShootingDisabled) return;
      setStartShootingDisabled(true);
      setStartShootingButtonText("Initializing...");

      // Step 1: Generate or find job number
      if (!jobData.jobNumber) {
        setStartShootingButtonText("Getting Job Number...");

        let assignedJobNumber = null;

        // Check if this is a multi-day job
        const isMultiDay = jobData.isMultiDay || jobData.parentJobId || (jobData.multiDayChildren && jobData.multiDayChildren.length > 0);

        // If multi-day, check if any sibling already has a job number
        if (isMultiDay && allMultipleDayJobs.length > 1) {
          for (const dayJob of allMultipleDayJobs) {
            if (dayJob.jobNumber && dayJob.id !== jobData.id) {
              assignedJobNumber = parseInt(dayJob.jobNumber);
              setStartShootingButtonText("Using existing job number...");
              break;
            }
          }
        }

        // If no existing job number found, fetch a new one
        if (!assignedJobNumber) {
          const jobYear = new Date(jobData.bookedStart).getFullYear();
          const res = await fetch(
            "https://getlatestjobnumber-y5ybiyz7ga-uc.a.run.app",
            {
              method: "POST",
              mode: "cors",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ year: jobYear }),
            }
          ).then((res) => res.json());

          if (!res.jobNumber) {
            throw new Error("Failed to get job number from server");
          }

          assignedJobNumber = parseInt(res.jobNumber);
        }

        // Update all multi-day jobs with the job number
        if (isMultiDay) {
          setStartShootingButtonText("Syncing multi-day jobs...");

          const parentId = jobData.parentJobId || jobData.id;
          const parentJobRef = doc(db, "JobData", parentId);
          const parentJobSnap = await getDoc(parentJobRef);

          if (parentJobSnap.exists()) {
            const parentJobData = parentJobSnap.data();
            const childIds = parentJobData.multiDayChildren || [];

            // Update parent job
            await updateDoc(parentJobRef, { jobNumber: assignedJobNumber });

            if (parentJobData.client && parentJobData.jobTypeFull) {
              const jtsRaw = parentJobData.jobTypeFull?.match(/^.*\((-.*)\)/);
              const jts = jtsRaw ? jtsRaw[1] : "";
              const parentJobName = `${assignedJobNumber}-${parentJobData.client}${
                parentJobData.multipleDays ? `-${parentJobData.multipleDays}` : ""
              }${jts}`;
              await updateDoc(parentJobRef, { jobName: parentJobName });
            }

            // Update all child jobs
            await Promise.all(childIds.map(async (childId) => {
              const childRef = doc(db, "JobData", childId);
              const childSnap = await getDoc(childRef);

              if (childSnap.exists()) {
                const childData = childSnap.data();
                await updateDoc(childRef, { jobNumber: assignedJobNumber });

                if (childData.client && childData.jobTypeFull) {
                  const jtsRaw = childData.jobTypeFull?.match(/^.*\((-.*)\)/);
                  const jts = jtsRaw ? jtsRaw[1] : "";
                  const childJobName = `${assignedJobNumber}-${childData.client}${
                    childData.multipleDays ? `-${childData.multipleDays}` : ""
                  }${jts}`;
                  await updateDoc(childRef, { jobName: childJobName });
                }
              }
            }));
          }
        } else {
          // Single day job - update normally
          const jtsRaw = jobData.jobTypeFull.match(/^.*\((-.*)\)/);
          let jts = "";
          if (jtsRaw) {
            jts = jtsRaw[1];
          }

          const jn = `${assignedJobNumber}-${jobData.client}${
            jobData.multipleDays ? `-${jobData.multipleDays}` : ""
          }${jts}`;

          await updateDoc(
            doc(db, "JobData", jobData.id),
            { jobName: jn, jobNumber: assignedJobNumber },
            { merge: true }
          );
        }

        // Update local state
        jobData.jobNumber = assignedJobNumber;
      }

      setStartShootingButtonText("Setting up breakdown...");

      // Step 2: Initialize image breakdown
      const breakdown = [];

      if (clientData?.imageBreakdown) {
        clientData?.imageBreakdown.forEach((bri) => {
          const newitemObj = {
            id: bri.id,
            jobTypesFilter: bri.jobTypesFilter || [],
            description: bri.description,
            quantityProcessed: "",
            filesInPost: "",
            quantitySupplied: "",
            expectedPerHour: bri.expectedPerHour,
            costPerFile: bri.costPerFile,
            existingItem: true,
            itemHidden: false,
          };
          if (
            !jobData.imageBreakdown?.some((obj) => obj.id === newitemObj.id)
          ) {
            breakdown.push(newitemObj);
          }
        });
      }

      // Add default breakdown item if none exist
      if (breakdown.length === 0 && !jobData.imageBreakdown?.length) {
        breakdown.push({
          id: uuidv4(),
          description: "",
          jobTypesFilter: [],
          quantityProcessed: "",
          filesInPost: "",
          quantitySupplied: "",
          expectedPerHour: "",
          costPerFile: "",
          existingItem: false,
          itemHidden: false,
        });
      }

      // Update job data with breakdown
      await updateDoc(
        doc(db, "JobData", jobData.id),
        {
          imageBreakdown: [...(jobData.imageBreakdown || []), ...breakdown],
          backgroundRolls: [],
          catering: [],
        },
        { merge: true }
      );

      setStartShootingButtonText("Adding to workflow...");

      // Step 3: Add to first list
      if (!lists[0].items.includes(jobData.id)) {
        lists[0].items.push(jobData.id);
      }

      // Remove duplicates
      lists[0].items = [...new Set(lists[0].items)];

      await updateDoc(doc(db, "AdminCollection", "lists"), { lists: lists });

      // Step 4: Handle express jobs
      if (jobData.express) {
        if (!jobData?.idLabels?.includes("5f0e8630fb83455e28da9908")) {
          await updateDoc(
            doc(db, "JobData", jobData.id),
            { idLabels: arrayUnion("5f0e8630fb83455e28da9908") }
          );
        }
      }

      setStartShootingButtonText("Creating folders...");

      // Step 5: Dropbox automation
      const isProductJob = jobData.jobTypeShort?.includes("-P");
      const isVideoJob = jobData.jobTypeShort?.includes("-V") || jobData.jobTypeShort?.includes("-E");
      
      const basePath = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort}${jobData.multipleDays ? `/${jobData.multipleDays}` : ``}`;
      const basePathNoMultipleDays = `/${jobData.client}/${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort}`;
      
      // Create base folder
      await getDropboxUrlWithRetry(`${basePath}`);
      
      if (isVideoJob) {
        // Video jobs only get Supply folder
        await getDropboxUrlWithRetry(`${basePathNoMultipleDays}/Supply`);
      } else if (isProductJob) {
        // Product jobs get Supply and Storyboards (no Storyboard Selects or Quickproofs)
        await getDropboxUrlWithRetry(`${basePathNoMultipleDays}/Supply`);
        await getDropboxUrlWithRetry(`${basePath}/Storyboards`);
      } else {
        // All other jobs get all folders: Supply, Storyboards, Storyboard Selects, Quickproofs
        await getDropboxUrlWithRetry(`${basePathNoMultipleDays}/Supply`);
        await getDropboxUrlWithRetry(`${basePath}/Storyboards`);
        await getDropboxUrlWithRetry(`${basePath}/Storyboard Selects`);
        await getDropboxUrlWithRetry(`${basePath}/Quickproofs`);
      }

      // Step 6: Electron integration
      if (isElectron && shouldCreateSession) {
        setStartShootingButtonText("Creating session...");
        
        window.ipcRenderer?.send("createSession", {
          jobName: getJobName(jobData),
          cardID: jobData.id,
          jobData: jobData,
        });

        // Mark as in shooting
        await updateDoc(
          doc(db, "JobData", jobData.id),
          {
            isInShooting: true,
            isInShootingTime: DateTime.now()
              .setZone("Australia/Sydney")
              .startOf("day")
              .toUTC()
              .toISO(),
          }
        );
      }

      setStartShootingButtonText("Setup Complete!");

      // Reset button after delay
      setTimeout(() => {
        setStartShootingButtonText("Start Shooting");
        setStartShootingDisabled(false);
      }, 3000);

    } catch (error) {
      console.error("Start shooting error:", error);
      setStartShootingButtonText("Start Shooting");
      setStartShootingDisabled(false);
    }
  };

  if (!shouldShowButton) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 168,
            zIndex: 45,
            backgroundColor:
              theme === "dark"
                ? "rgba(28, 28, 28, 0.98)"
                : "rgba(250, 250, 250, 0.98)",
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${
              theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
            }`,
            padding: 1.5,
            marginLeft: -1,
            marginRight: -1,
            marginTop: -1,
            marginBottom: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={startShooting}
              disabled={startShootingDisabled}
              sx={{
                width: { xs: "100%", sm: 280 },
                height: 60,
                fontSize: 18,
                fontWeight: "bold",
                borderRadius: 3,
                color: "#ffffff",
                background: startShootingDisabled
                  ? undefined
                  : "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                boxShadow: startShootingDisabled
                  ? undefined
                  : "0 4px 20px rgba(25, 118, 210, 0.3)",
                "&:hover": {
                  background: startShootingDisabled
                    ? undefined
                    : "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                  boxShadow: "0 6px 25px rgba(25, 118, 210, 0.4)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {startShootingDisabled ? (
                <CircularProgress size={24} style={{ color: "#ffffff" }} />
              ) : (
                startShootingButtonText
              )}
            </Button>

            {isElectron && !jobData?.jobTypeFull?.includes("-V") && !jobData?.jobTypeFull?.includes("-E") && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldCreateSession}
                    onChange={(e) => setShouldCreateSession(e.target.checked)}
                    size="small"
                    sx={{
                      color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                      "&.Mui-checked": {
                        color: theme === "dark" ? "#90caf9" : "#1976d2",
                      },
                    }}
                  />
                }
                label="Create Session"
                sx={{ 
                  fontSize: "0.875rem",
                  color: theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.7)",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};

export default StartShootingButton;